import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

/**
 * Renders a structured legal document (src/lib/legal/*Content.js) as semantic, real-text HTML:
 * one h1, one h2 per numbered section, real lists and tables, prose capped at 70ch.
 * Inline markup supported in strings: **bold**, [text](href); the bare support address auto-links.
 */
const INLINE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|support@ratecurrent\.com)/g;
const LINK_CLS = "underline underline-offset-2 text-[#15803D] hover:text-[#0A2540] break-words";

function Inline({ text }) {
  return text.split(INLINE).map((part, i) => {
    if (!part) return null;
    if (part.startsWith("**")) return <strong key={i} className="font-semibold text-[#0A2540]"><Inline text={part.slice(2, -2)} /></strong>;
    if (part === "support@ratecurrent.com") return <a key={i} href="mailto:support@ratecurrent.com" className={LINK_CLS}>{part}</a>;
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) {
      return m[2].startsWith("/")
        ? <Link key={i} to={m[2]} className={LINK_CLS}>{m[1]}</Link>
        : <a key={i} href={m[2]} className={LINK_CLS} rel="noopener noreferrer">{m[1]}</a>;
    }
    return part;
  });
}

function Block({ block }) {
  switch (block.t) {
    case "p":
      return <p className="mt-4"><Inline text={block.text} /></p>;
    case "h3":
      return <h3 className="font-display text-lg font-bold text-[#0A2540] mt-6">{block.text}</h3>;
    case "ul":
      return (
        <ul className="mt-4 space-y-2 list-disc pl-6 marker:text-[#16A34A]">
          {block.items.map((t, i) => <li key={i}><Inline text={t} /></li>)}
        </ul>
      );
    case "ol":
      return (
        <ol className="mt-4 space-y-3 list-decimal pl-6 marker:font-semibold marker:text-[#15803D]">
          {block.items.map((t, i) => <li key={i}><Inline text={t} /></li>)}
        </ol>
      );
    case "quote":
      return (
        <blockquote className="mt-6 border-l-4 border-[#16A34A] bg-[#F7F9FC] rounded-r-2xl px-5 py-4 text-[#0A2540]">
          <Inline text={block.text} />
        </blockquote>
      );
    case "address":
      return (
        <address className="mt-4 not-italic space-y-1">
          {block.lines.map((l, i) => <div key={i}><Inline text={l} /></div>)}
        </address>
      );
    case "small":
      return <p className="mt-3 text-xs leading-5 text-slate-500" data-testid="legal-postal-line">{block.text}</p>;
    case "table":
      return (
        <div
          className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A2540]"
          role="region"
          aria-label={block.caption}
          tabIndex={0}
        >
          <table className="w-full min-w-[34rem] text-left text-sm leading-6">
            <caption className="sr-only">{block.caption}</caption>
            <thead className="bg-[#F7F9FC] text-[#0A2540]">
              <tr>{block.head.map((h) => <th key={h} scope="col" className="px-4 py-3 font-semibold align-bottom">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) =>
                    c === 0
                      ? <th key={c} scope="row" className="px-4 py-3 font-semibold text-[#0A2540] align-top"><Inline text={cell} /></th>
                      : <td key={c} className="px-4 py-3 align-top"><Inline text={cell} /></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export default function LegalDocument({ doc, path, description, testId }) {
  return (
    <div data-testid={testId}>
      <Seo title={doc.title} description={description} path={path} />
      <header className="bg-[#F7F9FC] border-b border-slate-100">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-14 md:py-16">
          <span className="pill mb-5">Legal</span>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight font-extrabold leading-[1.05] text-[#0A2540]">{doc.title}</h1>
          <p className="mt-5 text-base text-slate-700" data-testid="legal-dates">
            RateCurrent LLC <span aria-hidden="true">·</span> Effective Date: {doc.effectiveDate} <span aria-hidden="true">·</span> Last Updated: {doc.lastUpdated}
          </p>
        </div>
      </header>

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 md:py-24 grid grid-cols-12 gap-x-10 gap-y-12">
        <nav aria-label={`${doc.title} sections`} className="col-span-12 lg:col-span-4 lg:order-2">
          <div className="lg:sticky lg:top-24">
            <p className="text-overline text-slate-500 mb-3">On This Page</p>
            <ol className="space-y-2 text-sm">
              <li><a href="#short-version" className="text-slate-700 hover:text-[#0A2540] hover:underline">The Short Version</a></li>
              {doc.sections.map((s) => (
                <li key={s.id}><a href={`#${s.id}`} className="text-slate-700 hover:text-[#0A2540] hover:underline">{s.heading}</a></li>
              ))}
            </ol>
          </div>
        </nav>

        <article className="col-span-12 lg:col-span-8 lg:order-1 max-w-[70ch] text-base leading-7 text-slate-700">
          <section id="short-version" aria-labelledby="short-version-heading" className="scroll-mt-24 rounded-3xl bg-[#E8EEF6] p-6 md:p-8">
            <h2 id="short-version-heading" className="font-display text-2xl font-bold tracking-tight text-[#0A2540]">The Short Version</h2>
            <ul className="mt-4 space-y-2 list-disc pl-6 marker:text-[#16A34A]">
              {doc.shortVersion.map((t, i) => <li key={i}><Inline text={t} /></li>)}
            </ul>
          </section>

          {doc.sections.map((s) => (
            <section key={s.id} id={s.id} aria-labelledby={`${s.id}-heading`} className="scroll-mt-24 mt-14">
              <h2 id={`${s.id}-heading`} className="font-display text-2xl font-bold tracking-tight text-[#0A2540]">{s.heading}</h2>
              <span className="mt-3 block h-[3px] w-8 rounded-full bg-[#16A34A]" aria-hidden="true" />
              {s.blocks.map((b, i) => <Block key={i} block={b} />)}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}
